import { forwardRef, useEffect, useRef, useState, useId, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { AlertTriangle } from 'lucide-react';
import { parseTypedAmount } from '@/lib/number';

interface WarningRange {
  min?: number;
  max?: number;
}

interface NumericInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'onBlur' | 'value'> {
  label: string;
  unit?: string;
  warningRange?: WarningRange;
  value?: number | null;
  /** Whole numbers only; a comma is a thousands separator ("350,000"). */
  integer?: boolean;
  /** The sample number in the whole-number refusal ("Enter a whole number, like 100"). */
  example?: string;
  /** A blank box is refused ("<label> can't be empty") and nothing is committed; for a saved value that cannot be removed. */
  required?: boolean;
  /** A reason the number is not acceptable (null = fine). Shown inline; nothing is committed. */
  validate?: (value: number) => string | null;
  /** Fires when the field enters or leaves the refused state (its message is showing). */
  onRefusalChange?: (refused: boolean) => void;
  onValueChange?: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      label,
      unit,
      warningRange,
      integer,
      example,
      required,
      validate,
      onRefusalChange,
      onFocus: onFocusProp,
      'aria-describedby': describedByProp,
      onValueChange,
      onBlur,
      value,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const id = useId();
    const propText = value !== undefined && value !== null ? String(value) : '';
    const [localValue, setLocalValue] = useState<string>(propText);
    const [isFocused, setIsFocused] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);
    const errorId = useId();

    const refusalChangeRef = useRef(onRefusalChange);
    useEffect(() => {
      refusalChangeRef.current = onRefusalChange;
    });
    useEffect(() => {
      refusalChangeRef.current?.(refusal !== null);
    }, [refusal]);
    useEffect(() => () => refusalChangeRef.current?.(false), []);

    // Issue #116: while not focused, show the prop (e.g. the server-rounded value
    // after save); the local edit only exists between focus and blur. A refused entry
    // stays on screen with its message until it is corrected.
    const displayValue = isFocused || refusal ? localValue : propText;

    // Check if current value is outside warning range
    const parsed = refusal ? null : parseTypedAmount(displayValue, { integer, example });
    const numericValue = parsed && 'value' in parsed ? parsed.value : null;
    const isOutsideRange =
      warningRange &&
      numericValue !== null &&
      ((warningRange.min !== undefined && numericValue < warningRange.min) ||
        (warningRange.max !== undefined && numericValue > warningRange.max));

    // Recommended range shown inline at all times (not just on violation).
    const hasRange =
      warningRange?.min !== undefined && warningRange?.max !== undefined;
    const rangeText = hasRange ? `${warningRange!.min}–${warningRange!.max}` : null;

    // Every quantity on these forms is zero or more; a field that allows negatives passes min.
    const reasonFor = (n: number | null): string | null => {
      if (n === null) return required ? `${label} can't be empty` : null;
      if (n < 0 && !(props.min !== undefined && Number(props.min) < 0)) return `${label} can't be negative`;
      return validate?.(n) ?? null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      setRefusal(null);

      if (onValueChange) {
        // What is typed so far is only ever reported when it is a whole, valid number
        // (or blank). Anything else reports the saved value back, so leaving the page
        // mid-typing cannot commit a partial parse like 3 from "3,a".
        const result = parseTypedAmount(newValue, { integer, example });
        const acceptable = 'value' in result && reasonFor(result.value) === null;
        onValueChange(acceptable ? result.value : (value ?? null));
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      const result = parseTypedAmount(localValue, { integer, example });
      if ('error' in result) {
        // "-5" in a whole-number field is a negative, not a malformed number.
        const negative = integer && /^-\s*\d[\d,]*$/.test(localValue.trim()) && !(props.min !== undefined && Number(props.min) < 0);
        setRefusal(negative ? "Count can't be negative" : result.error);
        return;
      }
      // A prefilled value the user didn't touch isn't re-judged (e.g. a saved 0 weight).
      const reason = localValue === propText ? null : reasonFor(result.value);
      if (reason) {
        setRefusal(reason);
        return;
      }
      setRefusal(null);
      if (onBlur) {
        onBlur(result.value);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocusProp?.(e);
      setIsFocused(true);
      if (!refusal) setLocalValue(propText);
    };

    return (
      <div className={cn('space-y-1.5', className)}>
        <label
          htmlFor={id}
          className="flex items-center justify-between text-sm font-medium text-gray-700"
        >
          <span>{label}</span>
          {unit && (
            <span className="text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {unit}
            </span>
          )}
        </label>

        <div className="relative">
          <input
            ref={ref}
            id={id}
            type="text"
            inputMode={integer ? 'numeric' : 'decimal'}
            autoComplete="off"
            aria-invalid={refusal ? true : undefined}
            aria-describedby={[describedByProp, refusal && errorId].filter(Boolean).join(' ') || undefined}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            className={cn(
              // Base styles
              'block w-full rounded-lg px-3.5 py-3',
              'text-gray-900 text-base font-medium tabular-nums',
              'placeholder:text-gray-400',
              // Border and background
              'bg-white border-2',
              // State-based border colors
              isOutsideRange && !isFocused
                ? 'border-amber-300 bg-amber-50/30'
                : isFocused
                  ? 'border-primary-500 ring-4 ring-primary-500/10'
                  : 'border-gray-200 hover:border-gray-300',
              // Transitions
              'transition-all duration-150',
              // Disabled state
              'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200',
              // Focus outline removal (using ring instead)
              'focus:outline-none'
            )}
            {...props}
          />

          {/* Warning indicator */}
          {isOutsideRange && !isFocused && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
          )}
        </div>

        {refusal && (
          <p id={errorId} role="alert" className="flex items-center gap-1.5 text-xs text-red-600">
            <AlertTriangle className="w-3.5 h-3.5" />
            {refusal}
          </p>
        )}

        {/* Recommended-range hint (always) / out-of-range warning (non-blocking) */}
        {refusal ? null : isOutsideRange ? (
          <p className="flex items-center gap-1.5 text-xs text-amber-600">
            <AlertTriangle className="w-3.5 h-3.5" />
            Outside recommended range
            {rangeText && <span className="text-amber-500">({rangeText})</span>}
          </p>
        ) : (
          rangeText && (
            <p className="text-xs text-gray-400">Recommended: {rangeText}</p>
          )
        )}
      </div>
    );
  }
);

NumericInput.displayName = 'NumericInput';
